import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import sanitizeHtml from 'sanitize-html'

import { getAccessToken } from 'cng-common/helpers/access'
import { proxy } from 'core/classes/helpers/UrlHelper'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'

import { CAMPAIGN_ATTACHMENTS_FORM_ID } from '../config'
import { CampaignDocument } from '../model/campaign'

// Raw attachment row shape used for updates.
export type RelevantDocumentRaw = {
  EntryId: number
  FileName: string
  FileType: string
  FileLink: string
  Countries: string
  IsVisible: 1 | 0
}

// Raw payload for creating an attachment row.
export type UploadDocument = {
  FileName: string
  FileType: string
  File: File
  CampaignId: number
  Countries: string
  IsVisible: number
}

// URL the file bytes of an attachment row are POSTed to.
export const campaignAttachmentHandlerUrl = (entryId: number) =>
  proxy(`/form/${CAMPAIGN_ATTACHMENTS_FORM_ID}/attachments/${entryId}/File/handler`)

// Download/view link of an attachment file.
export const campaignAttachmentFileLink = (uid: string, extension: string) =>
  proxy(`/form/${CAMPAIGN_ATTACHMENTS_FORM_ID}/links/v2/${uid}/File/${extension}`)

const getAttachmentSource = () => CrudStore.get(CrudStoreItem.CampaignAttachments)

// Sends the file bytes of an existing attachment row to the form file handler.
const postCampaignAttachmentFile = async (entryId: number, file: File, fileName: string) => {
  const url = campaignAttachmentHandlerUrl(entryId)
  const data = new FormData()
  data.append('File', file, fileName)

  return fetch(sanitizeHtml(url), {
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
    method: 'POST',
    body: data,
  })
}

// Creates the attachment row, then uploads the file bytes.
export const uploadCampaignAttachment = async (attachment: UploadDocument) => {
  const attachmentSource = getAttachmentSource()
  const [created] = await attachmentSource.create(CAMPAIGN_ATTACHMENTS_FORM_ID, [attachment])

  await postCampaignAttachmentFile(created.EntryId, attachment.File, attachment.FileName)

  return created
}

// Updates an attachment row.
export const updateCampaignAttachment = (payload: RelevantDocumentRaw) =>
  getAttachmentSource().update(CAMPAIGN_ATTACHMENTS_FORM_ID, [payload])

// Deletes an attachment row by its entry id.
export const deleteCampaignAttachment = (entryId: number) =>
  getAttachmentSource().delete(CAMPAIGN_ATTACHMENTS_FORM_ID, { EntryId: entryId })

// Rename is implemented as re-create: fetch the existing file bytes, create a
// new attachment row with the new name, upload the bytes, then delete the old
// row. Both failure paths roll back the newly created row so no duplicates are
// left behind.
export const renameCampaignAttachment = async (document: CampaignDocument) => {
  const token = getAccessToken()

  const response = await fetch(sanitizeHtml(document.fileLink), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    method: 'GET',
  })
  if (!response.ok) {
    throw new Error('Failed to fetch the document for renaming')
  }

  const blob = await response.blob()
  const file = new File([blob], document.fileName, {
    type: document.fileType,
  })

  const attachmentSource = getAttachmentSource()
  const [created] = await attachmentSource.create(CAMPAIGN_ATTACHMENTS_FORM_ID, [
    {
      FileName: file.name,
      FileType: file.type,
      File: file,
      CampaignId: document.campaignId,
      Countries: document.countries.join(','),
      IsVisible: document.isVisible ? 1 : 0,
    },
  ])

  const uploadResponse = await postCampaignAttachmentFile(created.EntryId, file, file.name)

  if (!uploadResponse.ok) {
    try {
      await attachmentSource.delete(CAMPAIGN_ATTACHMENTS_FORM_ID, [created.EntryId])
    } catch (e) {
      Logger.error('Failed to cleanup partially uploaded attachment', e)
      throw e
    }
    throw new Error('Failed to upload renamed document')
  }

  try {
    await attachmentSource.delete(CAMPAIGN_ATTACHMENTS_FORM_ID, { EntryId: document.entryId })
  } catch (err) {
    try {
      await attachmentSource.delete(CAMPAIGN_ATTACHMENTS_FORM_ID, { EntryId: created.EntryId })
    } catch (e) {
      Logger.error('Failed to rollback new attachment after delete failure', e)
      throw e
    }
    throw err
  }

  return created
}
